import { ComponentProps, FC, ReactNode, useId } from 'react';
import {
  formControlLabelClasses,
  InputLabel,
  Stack,
  styled,
  svgIconClasses,
  TextFieldProps,
} from '@mui/material';

import { Checkbox, TextFieldWithDebounce } from '@components/base';

export type EnhancedTextFieldProps = Pick<
  TextFieldProps,
  'disabled' | 'error' | 'helperText' | 'id' | 'name' | 'required' | 'value'
> &
  ComponentProps<typeof TextFieldWithDebounce> & {
    CheckboxProps?: ComponentProps<typeof Checkbox>;
    initInheritFromDefaultLang?: boolean;
    label: ReactNode;
    /**
     *
     */
    mode: 'auto' | 'manual';
  };

const TinyCheckbox = styled(Checkbox)(({ theme }) => ({
  marginTop: theme.spacing(-1),
  [`.${svgIconClasses.root}`]: {
    fontSize: 14,
  },
  [`.${formControlLabelClasses.root}`]: {
    marginRight: theme.spacing(),
    whiteSpace: 'nowrap',
  },
  [`.${formControlLabelClasses.label}`]: {
    fontSize: theme.typography.caption.fontSize,
  },
}));

const EnhancedTextField: FC<EnhancedTextFieldProps> = ({
  CheckboxProps: checkboxProps,
  disabled,
  error,
  id: customId,
  label,
  mode,
  onChange,
  required,
  value,
  ...textFieldProps
}) => {
  const id = useId();
  const actualId = customId ?? id;

  return (
    <>
      <Stack alignItems='flex-start' spacing={-0.5}>
        <Stack direction='row' justifyContent='space-between' width={1}>
          <Stack
            component={InputLabel}
            direction='row'
            disabled={disabled}
            error={error}
            htmlFor={actualId}
            required={required}
            shrink
          >
            {label}
          </Stack>

          {checkboxProps && <TinyCheckbox {...checkboxProps} />}
        </Stack>

        <TextFieldWithDebounce
          {...textFieldProps}
          disabled={disabled}
          error={error}
          fullWidth
          hiddenLabel
          id={actualId}
          onChange={e => {
            onChange?.(e);
            checkboxProps?.onChange?.(e, false);
          }}
          required={required}
          value={value}
        />
      </Stack>
    </>
  );
};

export { EnhancedTextField };
